// blob.frag — N-blob SDF metaball + slow-ellipse single-octave warp +
// multi-hotspot OKLab gradient + rectangular container clip.
//
// Layer architecture (SPEC §4.1):
//   1. Paper-white + grain (constant 0.012 amplitude)
//   2. Optional background media — image or video (cover/contain/stretch,
//      sRGB-linear blend)
//   3. Blobs — chain-smin'd up to MAX_BLOBS, then SDF-clipped to the
//      rectangular container so the body flattens against walls
//      mathematically (no scripted "wall warp" effect)
//   4. Typography — DOM overlay (composited only at PNG export time)

precision highp float;

#define MAX_BLOBS 10
#define MAX_HOTSPOTS 24

#include 'chunks/noise.glsl'
#include 'chunks/sdf.glsl'
#include 'chunks/oklch.glsl'

varying vec2 vUv;

uniform vec2  uResolution;
uniform float uTime;

uniform vec2  uBlobPos[MAX_BLOBS];
uniform float uBlobRadius[MAX_BLOBS];
uniform float uWarpSeed[MAX_BLOBS];
uniform float uSminK[MAX_BLOBS];        // chain smin smoothness; slot 0 unused
uniform float uVibEnergy[MAX_BLOBS];    // per-blob breathing modulation
uniform int   uActiveBlobs;

uniform vec3  uHotspotsLCH[MAX_HOTSPOTS];
uniform vec2  uHotspotsUV[MAX_HOTSPOTS];
uniform float uHotspotIntensity[MAX_HOTSPOTS];
uniform float uHotspotsBlobId[MAX_HOTSPOTS];
uniform int   uActiveHotspots;

uniform vec3  uBackgroundColor;
uniform float uPixelRatio;
uniform float uLineWidth;
uniform float uMarginWidth;        // CSS px transparent gap between line and fill

// Background media layer (image or video; both bind through sampler2D).
uniform sampler2D uBgTexture;
uniform float     uBgEnabled;
uniform float     uBgOpacity;
uniform int       uBgFitMode;
uniform vec2      uBgMediaSize;

// Phase 4 — halftone fill layer.
uniform float uHalftoneEnabled;        // 0/1
uniform float uHalftoneMode;           // 0=oneColor, 1=twoColor, 2=grayscale
uniform float uHalftoneShape;          // 0=circle, 1=square, 2=diamond, 3=triangle, 4=plus
uniform float uHalftoneCellPx;         // FHD-CSS px (cell-to-cell distance)
uniform float uHalftoneGap;            // 0.3..1.0 (dot max-extent fraction of inradius)
uniform float uHalftoneL;              // OKLCH L (channel preset midpoint)
uniform float uHalftoneC;              // OKLCH C (channel preset midpoint)
uniform float uHalftoneHueOne_rad;     // 0..2π (1-color mode)
uniform float uHalftoneHueTwoA_rad;    // 0..2π (2-color mode, top)
uniform float uHalftoneHueTwoB_rad;    // 0..2π (2-color mode, bottom)

// Constant paper-white grain amplitude (UI no longer exposes it).
const float GRAIN_AMOUNT = 0.012;
// Smooth-max width when clipping the combined SDF to the container.
const float CONTAINER_K = 0.04;
// Phase 4 fixup — FHD short-edge reference (CSS px). All UI-px values
// (line, margin, halftone cell) are interpreted as "fraction of the
// FHD 1080-px short edge"; the shader scales them into device pixels
// via fhdScale below so the visual proportion is identical across
// FHD live preview, FHD/4K/Print exports.
const float FHD_REFERENCE = 1080.0;
// Default occupancy when no background media is loaded — yields a
// regular polka-dot pattern instead of a flat tint.
const float FLAT_FILL_OCCUPANCY = 0.6;
// Halftone grid orientation, fixed at 45° as the production-finalised
// art direction (the 0°/90° toggle was removed; this const is kept
// named so a future re-promotion to a uniform stays a one-line edit).
const float HALFTONE_ANGLE_RAD = 0.7853981633974483; // π / 4

vec2 uvToWorld(vec2 uv) {
  vec2 ndc = uv * 2.0 - 1.0;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  if (aspect >= 1.0) {
    ndc.x *= aspect;
  } else {
    ndc.y /= aspect;
  }
  return ndc;
}

// ─── Phase 4 helpers ────────────────────────────────────────────────────────

// Background-media sampleUV computation. Extracted so we can re-evaluate
// the same fit/aspect math at any source UV (current pixel for the
// existing fill path, dot-centre UV for the halftone occupancy probe).
// Returns sampleUV unchanged for stretch (mode 2). For contain (mode 1)
// the caller can compare against [0,1] to detect bars.
vec2 bgFitSampleUV(vec2 srcUV) {
  float frameAspect = uResolution.x / max(uResolution.y, 1.0);
  float imgAspect   = uBgMediaSize.x / max(uBgMediaSize.y, 1.0);
  vec2 sampleUV = srcUV;
  if (uBgFitMode == 0) {                 // cover
    if (imgAspect > frameAspect) {
      float scale = frameAspect / imgAspect;
      sampleUV.x = (srcUV.x - 0.5) * scale + 0.5;
    } else {
      float scale = imgAspect / frameAspect;
      sampleUV.y = (srcUV.y - 0.5) * scale + 0.5;
    }
  } else if (uBgFitMode == 1) {          // contain
    if (imgAspect > frameAspect) {
      float scale = imgAspect / frameAspect;
      sampleUV.y = (srcUV.y - 0.5) * scale + 0.5;
    } else {
      float scale = frameAspect / imgAspect;
      sampleUV.x = (srcUV.x - 0.5) * scale + 0.5;
    }
  }
  return sampleUV;
}

// Pointy-top hex grid: nearest cell centre in pixel space.
// `spacing` is the cell-to-cell distance in pixels. Uses canonical cube-
// coordinate rounding (https://www.redblobgames.com/grids/hexagons/).
//
// We rely on `floor(x + 0.5)` instead of `round()` so the function
// compiles on WebGL 1.0 contexts where `round` is unavailable.
vec2 hexNearestCenter(vec2 p, float spacing) {
  float s = spacing / sqrt(3.0);          // hex side length
  float q = (sqrt(3.0) / 3.0 * p.x - p.y / 3.0) / s;
  float r = (p.y * 2.0 / 3.0) / s;
  // Cube coords (x + y + z = 0).
  float xc = q;
  float zc = r;
  float yc = -xc - zc;
  float rx = floor(xc + 0.5);
  float ry = floor(yc + 0.5);
  float rz = floor(zc + 0.5);
  float dx = abs(rx - xc);
  float dy = abs(ry - yc);
  float dz = abs(rz - zc);
  if (dx > dy && dx > dz) {
    rx = -ry - rz;
  } else if (dy > dz) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }
  return vec2(
    sqrt(3.0) * s * rx + sqrt(3.0) / 2.0 * s * rz,
    1.5 * s * rz
  );
}

// ─── Phase 4.5 dot-shape SDFs ───────────────────────────────────────────────
// All shapes are evaluated in the *unrotated* screen frame so that the
// hex grid's 45° rotation does not double-rotate the shape itself —
// otherwise an axis-aligned square would render as a diamond and lose
// its visual identity. `disp` is `p_pixel - center_pixel` (i.e. the
// vector from the dot's centre to the current fragment, in framebuffer
// pixels). Each shape's `sizeMax` is calibrated so that at occupancy
// = 1 its furthest point sits at `inradius × Gap` from the centre,
// keeping the Gap = breathing-room semantic identical across shapes.
//
// Within a shape, area scales linearly with occupancy (sizeMax
// multiplied by sqrt(A)).

// Axis-aligned square (iq's box SDF). half-side = sizeMax / sqrt(2)
// puts the corner at sizeMax (= the calibrated max extent).
float sdfHalftoneSquare(vec2 disp, float sizeMax) {
  float halfSide = sizeMax * 0.7071067811865476; // 1/sqrt(2)
  vec2 d = abs(disp) - vec2(halfSide);
  return length(max(d, vec2(0.0))) + min(max(d.x, d.y), 0.0);
}

// 45°-rotated square (taxicab norm). half-diagonal = sizeMax → vertex
// at sizeMax along ±x, ±y axes.
float sdfHalftoneDiamond(vec2 disp, float sizeMax) {
  vec2 q = abs(disp);
  return q.x + q.y - sizeMax;
}

// Upward-pointing equilateral triangle (centroid at the dot centre,
// top vertex on +y). Constructed from three half-plane distances,
// max-of-three: positive outside, negative inside. Accurate enough
// near the edge (where the AA band lives) for the 1-pixel smoothstep.
//
// circumradius = sizeMax → top vertex at (0, sizeMax). Inradius =
// sizeMax / 2. Edge equations derived from the three vertices
// (0, sizeMax), (±√3/2 · sizeMax, -sizeMax/2).
float sdfHalftoneTriangle(vec2 disp, float sizeMax) {
  float halfSize = sizeMax * 0.5;
  // Right slant: (√3/2)·x + (1/2)·y - sizeMax/2 = 0
  // Left slant : -(√3/2)·x + (1/2)·y - sizeMax/2 = 0
  // Bottom     : -y - sizeMax/2 = 0
  // Each half-plane normal is a unit vector, so the signed distance
  // is the dot product minus the offset. The triangle's interior is
  // where all three are negative; max-of-three gives the SDF.
  float sqrt3_2 = 0.8660254037844386; // √3 / 2
  float dRight  =  sqrt3_2 * disp.x + 0.5 * disp.y - halfSize;
  float dLeft   = -sqrt3_2 * disp.x + 0.5 * disp.y - halfSize;
  float dBot    = -disp.y - halfSize;
  return max(max(dRight, dLeft), dBot);
}

// Axis-aligned plus / cross. 3×3 grid centre cross (5 cells), arm width
// = sizeMax * 2/3, arm length = sizeMax * 2 (full bounding-box width).
// Built as a union of two axis-aligned rectangles (horizontal and
// vertical bars) via min(boxSDF_h, boxSDF_v): each box SDF is negative
// inside, positive outside, so the min is the SDF of their union — same
// sign convention as the other halftone shape SDFs (inside < 0).
float sdfHalftonePlus(vec2 disp, float sizeMax) {
  float armHalf = sizeMax / 3.0;
  vec2 d = abs(disp);
  float horiz = max(d.x - sizeMax, d.y - armHalf);
  float vert  = max(d.x - armHalf, d.y - sizeMax);
  return min(horiz, vert);
}

// Halftone tint at the dot centre. Three modes:
//   0 oneColor   solid uHalftoneHueOne at the channel preset L/C.
//   1 twoColor   top-to-bottom OKLab interpolation between
//                uHalftoneHueTwoA (top) and uHalftoneHueTwoB (bottom);
//                each dot is one colour (sampled at its centre's Y).
//   2 grayscale  pure grey at the channel preset L (C forced to 0).
//                Mono channel was already near-grey but this is exact.
vec3 halftoneTintAt(vec2 centerPixel) {
  if (uHalftoneMode < 0.5) {
    // 1-color
    vec3 lab = oklch_to_oklab(uHalftoneL, uHalftoneC, uHalftoneHueOne_rad);
    return linear_to_srgb(oklab_to_linear_rgb(lab.x, lab.y, lab.z));
  } else if (uHalftoneMode < 1.5) {
    // 2-color: gl_FragCoord.y has 0 at the bottom of the framebuffer
    // in WebGL, so gradT = 1 - y/H makes top = 0 (hueTwoA) and bottom
    // = 1 (hueTwoB).
    float gradT = clamp(1.0 - centerPixel.y / max(uResolution.y, 1.0), 0.0, 1.0);
    vec3 lab_a = oklch_to_oklab(uHalftoneL, uHalftoneC, uHalftoneHueTwoA_rad);
    vec3 lab_b = oklch_to_oklab(uHalftoneL, uHalftoneC, uHalftoneHueTwoB_rad);
    vec3 lab = mix(lab_a, lab_b, gradT);
    return linear_to_srgb(oklab_to_linear_rgb(lab.x, lab.y, lab.z));
  } else {
    // grayscale: C forced to 0 (a/b both = 0 in OKLab), L from the
    // active channel preset. Channel switch keeps the grayscale tint
    // tracking the channel's lightness midpoint.
    vec3 lab = vec3(uHalftoneL, 0.0, 0.0);
    return linear_to_srgb(oklab_to_linear_rgb(lab.x, lab.y, lab.z));
  }
}

void main() {
  vec2 p = uvToWorld(vUv);

  // Frame half-extent (world-space). Matches uvToWorld's extent.
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 frameHalf = aspect >= 1.0 ? vec2(aspect, 1.0) : vec2(1.0, 1.0 / aspect);

  // FHD-CSS-px → device-px scale factor. UI values such as
  // `uLineWidth`, `uMarginWidth`, and `uHalftoneCellPx` are expressed
  // in "FHD short-edge" CSS pixels; multiplying by `fhdScale` maps
  // them into the actual framebuffer pixels regardless of whether
  // we're rendering live (dpr=2) or off-screen for FHD/4K/Print
  // export (dpr=1). This replaces the older `uPixelRatio`-based
  // path, which made 4K/Print exports render with visibly thinner
  // strokes than the live preview.
  float shortEdgeRes = min(uResolution.x, uResolution.y);
  float fhdScale = max(shortEdgeRes, 1.0) / FHD_REFERENCE;

  // Line-width in shader units. The container is inset by halfLine so the
  // stroke fits entirely inside the export frame.
  float lineWidthShader = (uLineWidth * fhdScale * 2.0) / max(shortEdgeRes, 1.0);
  float halfLine = 0.5 * lineWidthShader;

  // ---- 1. Combined SDF via chain smin --------------------------------------
  float d = 1e9;
  bool hasActiveBlob = false;
  for (int i = 0; i < MAX_BLOBS; i++) {
    if (i >= uActiveBlobs) break;
    float di = sdfWithWarp(
      p, uBlobPos[i], uBlobRadius[i], uTime,
      uWarpSeed[i], uVibEnergy[i]
    );
    if (!hasActiveBlob) {
      d = di;
      hasActiveBlob = true;
    } else {
      d = smin(d, di, uSminK[i]);
    }
  }

  // ---- 1b. Container clip (smooth max with rectangle SDF) ------------------
  // Rectangle SDF: negative inside, positive outside. The blob may only exist
  // inside the inset rectangle (frame minus halfLine on every side), so we
  // smooth-max(blobD, containerD). This is what flattens the body against
  // walls — no scripted wall warp, just the SDF math.
  if (hasActiveBlob) {
    vec2 containerHalf = max(frameHalf - vec2(halfLine), vec2(0.0));
    vec2 q = abs(p) - containerHalf;
    float containerD = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
    d = smax(d, containerD, CONTAINER_K);
  }

  // ---- 2. Background base (paper white + constant grain) -------------------
  // Layer 1 only. The optional bg media is no longer painted across the
  // whole frame — it appears only inside the blob's fill region (see
  // step 4 below: fillContentColor).
  vec3 bgColor = uBackgroundColor;
  bgColor *= 1.0 + (hash21(p * 137.0) - 0.5) * GRAIN_AMOUNT;

  // ---- 2b. Background media as fill content -------------------------------
  // Compute the media colour at this fragment, BEFORE knowing whether the
  // pixel is inside the fill region. The decision to use it (or fall back
  // to bgColor for the no-media case / contain-mode bars) happens in
  // step 4's premultiplied composition.
  vec3 fillContentColor = bgColor;   // default: paper-white + grain
  if (uBgEnabled > 0.5) {
    vec2 sampleUV = bgFitSampleUV(vUv);
    bool valid = uBgFitMode != 1
      || (sampleUV.x >= 0.0 && sampleUV.x <= 1.0
          && sampleUV.y >= 0.0 && sampleUV.y <= 1.0);
    if (valid) {
      vec3 imgRaw = texture2D(uBgTexture, sampleUV).rgb;
      vec3 bgLin    = srgb_to_linear(bgColor);
      vec3 imgLin   = srgb_to_linear(imgRaw);
      vec3 mixedLin = mix(bgLin, imgLin, clamp(uBgOpacity, 0.0, 1.0));
      fillContentColor = linear_to_srgb(mixedLin);
    }
  }

  // ---- 3. Inside color: per-hotspot blob-frame evaluation ------------------
  float curlSpeed = 0.04;
  float curlAmp   = 0.12;

  vec2  internalUV[MAX_BLOBS];
  for (int i = 0; i < MAX_BLOBS; i++) {
    if (i >= uActiveBlobs) break;
    vec2 uvLocal = (p - uBlobPos[i]) / max(uBlobRadius[i], 1e-5);
    vec2 curl    = curlNoise2(uvLocal * 1.5 + vec2(uTime * curlSpeed));
    internalUV[i] = uvLocal + curl * curlAmp;
  }

  vec3  labAccum = vec3(0.0);
  float wAccum   = 0.0;
  const float SIGMA  = 0.6;
  const float INV_S2 = 1.0 / (SIGMA * SIGMA);

  for (int i = 0; i < MAX_HOTSPOTS; i++) {
    if (i >= uActiveHotspots) break;
    int bid = int(uHotspotsBlobId[i] + 0.5);
    if (bid < 0 || bid >= MAX_BLOBS) continue;
    if (bid >= uActiveBlobs) continue;
    vec2 internalUV_i = internalUV[bid];

    vec2 hsUV = uHotspotsUV[i];
    float dx  = internalUV_i.x - hsUV.x;
    float dy  = internalUV_i.y - hsUV.y;
    float d2  = dx * dx + dy * dy;
    float w   = uHotspotIntensity[i] * exp(-d2 * INV_S2);
    vec3 lab  = oklch_to_oklab(uHotspotsLCH[i].x, uHotspotsLCH[i].y, uHotspotsLCH[i].z);
    labAccum += lab * w;
    wAccum   += w;
  }

  vec3 insideColor;
  if (wAccum > 1e-6) {
    vec3 lab = labAccum / wAccum;
    vec3 lin = oklab_to_linear_rgb(lab.x, lab.y, lab.z);
    insideColor = linear_to_srgb(lin);
  } else {
    insideColor = uBackgroundColor;
  }

  // ---- 4. Stroke + margin + fill composition ------------------------------
  // Three-region blob:
  //   inLine  : pixelDist ∈ [-halfLinePx, +halfLinePx]   → opaque, line color
  //   inMargin: pixelDist ∈ [-halfLinePx-marginPx, -halfLinePx]
  //                                                       → TRANSPARENT (Layer 1 shows)
  //   inFill  : pixelDist ≤ -halfLinePx - marginPx       → opaque, fill color
  //   outside : pixelDist >  +halfLinePx                 → transparent
  //
  // The `max(dScreenLen, expectedGrad)` floor on the denominator keeps
  // pixelDist physical even where the chain smin / rectangle smax cancel
  // |∇d_screen| (saddle points, container corners). With pixelDist bounded,
  // a single 1-pixel-AA smoothstep per threshold suffices.
  vec2  dScreenGrad  = vec2(dFdx(d), dFdy(d));
  float dScreenLen   = length(dScreenGrad);
  float expectedGrad = 2.0 / max(shortEdgeRes, 1.0);

  float pixelDist  = d / max(dScreenLen, expectedGrad);
  float halfLinePx = uLineWidth * fhdScale * 0.5;
  float marginPx   = uMarginWidth * fhdScale;

  // "Inside threshold T" masks (1 when pixelDist << T, 0 when >> T).
  // sil   : T = +halfLinePx                          (silhouette)
  // mf    : T = -halfLinePx                          (line/margin edge)
  // fillR : T = -halfLinePx - marginPx               (margin/fill edge)
  float sil     = 1.0 - smoothstep(halfLinePx - 1.0, halfLinePx + 1.0, pixelDist);
  float mf      = 1.0 - smoothstep(-halfLinePx - 1.0, -halfLinePx + 1.0, pixelDist);
  float fillReg = 1.0 - smoothstep(-halfLinePx - marginPx - 1.0,
                                   -halfLinePx - marginPx + 1.0, pixelDist);

  // Region weights. Sum (line + margin + fill + outside) = 1.
  float inLine   = max(sil - mf, 0.0);
  float inFill   = fillReg;
  // (inMargin = mf - fillReg, but it contributes no opacity → not needed.)

  // ---- 4a. Phase 4 — halftone fill content (replaces fillContentColor
  // when uHalftoneEnabled). Hex-grid dots whose occupancy tracks the bg
  // media's OKLab L (smaller dots in light areas, bigger dots in dark
  // areas; bg-less mode forces 100 % occupancy = solid tint). Dot tint
  // is the channel-preset L/C combined with the picker hueA/B (see
  // halftoneTintAt). The line + margin pass above is unchanged.
  vec3 fillColor = fillContentColor;
  if (uHalftoneEnabled > 0.5) {
    // Pixel coordinates in the framebuffer (gl_FragCoord matches
    // uResolution). Rotate around the frame centre by -angle so a
    // positive angle visually rotates the grid CCW. Angle is locked
    // at 45° (HALFTONE_ANGLE_RAD).
    vec2 p_pixel = gl_FragCoord.xy;
    vec2 frameCenter = uResolution * 0.5;
    vec2 p_rel = p_pixel - frameCenter;
    float ca = cos(HALFTONE_ANGLE_RAD);
    float sa = sin(HALFTONE_ANGLE_RAD);
    vec2 p_rot = vec2(
      p_rel.x *  ca + p_rel.y * sa,
      p_rel.x * -sa + p_rel.y * ca
    );

    float spacing = uHalftoneCellPx * fhdScale;
    vec2 center_rot = hexNearestCenter(p_rot, spacing);

    // Un-rotate the centre back to pixel space for the bg sample / Y
    // gradient. (R(angle) * center_rot.)
    vec2 center_pixel = vec2(
      center_rot.x *  ca - center_rot.y * sa,
      center_rot.x *  sa + center_rot.y * ca
    ) + frameCenter;

    // Occupancy: bg media's perceptual lightness when a media source
    // is loaded; otherwise the FLAT_FILL_OCCUPANCY constant so the fill
    // reads as a regular polka-dot pattern instead of a flat tint.
    float occupancy = FLAT_FILL_OCCUPANCY;
    if (uBgEnabled > 0.5) {
      vec2 centerUV = center_pixel / max(uResolution, vec2(1.0));
      vec2 sampleUV = bgFitSampleUV(centerUV);
      bool valid = uBgFitMode != 1
        || (sampleUV.x >= 0.0 && sampleUV.x <= 1.0
            && sampleUV.y >= 0.0 && sampleUV.y <= 1.0);
      if (valid) {
        vec3 bgRaw = texture2D(uBgTexture, sampleUV).rgb;
        vec3 bgLin = srgb_to_linear(bgRaw);
        vec3 lab   = linear_rgb_to_oklab(bgLin);
        occupancy = clamp(1.0 - lab.x, 0.0, 1.0);
      } else {
        // Outside the contain-mode media bounds — paper-white only,
        // no dot.
        occupancy = 0.0;
      }
    }

    // Phase 4.5: per-shape sizing follows the max-extent-equalisation
    // rule. `sizeMax` is the distance from each dot's centre to its
    // furthest point at occupancy = 1; the same Gap value therefore
    // produces the same "breathing room" between adjacent dots no
    // matter which primitive is selected. Within a shape, area scales
    // linearly with occupancy via sqrt(A) on the size parameter.
    float inradius = spacing * 0.5;
    float sizeMax  = sqrt(occupancy) * inradius * uHalftoneGap;

    // Shape SDF in the *unrotated* screen frame (the hex grid is
    // rotated 45° but each individual dot keeps its canonical axis
    // alignment, so square stays a square and triangle stays
    // upward).
    vec2 disp = p_pixel - center_pixel;
    float sdf;
    if (uHalftoneShape < 0.5) {
      sdf = length(disp) - sizeMax;
    } else if (uHalftoneShape < 1.5) {
      sdf = sdfHalftoneSquare(disp, sizeMax);
    } else if (uHalftoneShape < 2.5) {
      sdf = sdfHalftoneDiamond(disp, sizeMax);
    } else if (uHalftoneShape < 3.5) {
      sdf = sdfHalftoneTriangle(disp, sizeMax);
    } else {
      sdf = sdfHalftonePlus(disp, sizeMax);
    }
    // 1-pixel AA on the shape's edge (sdf is in framebuffer pixels).
    float dotMask = 1.0 - smoothstep(-1.0, 1.0, sdf);

    vec3 tintColor = halftoneTintAt(center_pixel);
    // Gaps between dots show paper-white + grain (already in bgColor).
    fillColor = mix(bgColor, tintColor, dotMask);
  }

  // Premultiplied composition in linear space:
  //   final = bg * (1 − α) + lineLin·inLine + fillLin·inFill
  //   α     = inLine + inFill         (margin and outside add 0)
  vec3  lineColor = insideColor;
  float alpha     = hasActiveBlob ? clamp(inLine + inFill, 0.0, 1.0) : 0.0;

  vec3 bgLin    = srgb_to_linear(bgColor);
  vec3 lineLin  = srgb_to_linear(lineColor);
  vec3 fillLin  = srgb_to_linear(fillColor);
  vec3 mixedLin = bgLin * (1.0 - alpha) + lineLin * inLine + fillLin * inFill;
  vec3 finalColor = linear_to_srgb(mixedLin);

  gl_FragColor = vec4(finalColor, 1.0);
}
